/* =========================================================================
 * THE PERIOD TOTAL — what the SELECTED DATE PERIOD is worth, as a range.
 *
 * Brian: *"I want to be able to have my team sort by a date period for sum."*
 * A rep picks a window, the list shows what falls in it, and the sum says what
 * that window is worth. Change the period, the sum changes. That is the whole
 * feature.
 *
 * ⚠️ IT IS NOT A BOOK TOTAL, and the first cut of this file was. Brian:
 * *"I'm not asking for the entire book like you just made up."* A whole-book
 * figure answers "what am I sitting on", which is a manager's question asked
 * once; a period figure answers "what is in front of me this month", which is
 * the question a rep asks every week. The population this totals is whatever
 * the active period selected — nothing wider.
 *
 * 🔴 THE TRAP THIS MODULE EXISTS TO AVOID: the screen shows thirty rows and
 * the period may hold hundreds. A total over the VISIBLE slice would be wrong
 * QUIETLY, because a sum of thirty rows looks exactly like a sum of three
 * hundred. So `records` is returned beside every figure and the view can never
 * print a total without the count it covers.
 *
 * ⚠️ IT TOTALS, IT DOES NOT PRICE. Every figure comes from the per-record
 * `opportunityValue()` already computed once from the shared units table. No
 * price is read here and no unit count is multiplied — a second multiplication
 * is a second answer to the same question, and the one thing worse than no
 * total is two of them.
 * ========================================================================= */

/** Below this sample size a row is a sport-level average, not a comparable. */
import { COMPARABLE_N } from './opportunityValue.js'

const add = (a, b) => (a == null && b == null ? null : (a || 0) + (b || 0))

/**
 * Total a set of per-record values.
 *
 * `values` is any iterable of the `{ usd, low, high, confidence, units }`
 * shape `opportunityValue()` returns — including the null-figure ones, which
 * are COUNTED rather than skipped.
 *
 * Returns:
 *   `{ records, priced, unpriced, floor, mid, ceiling, ranged, point,
 *      thinUsd, thinShare, thinRecords, hasFigure }`
 */
export function periodTotal(values = []) {
  let records = 0
  let priced = 0
  let floor = null
  let mid = null
  let ceiling = null
  let ranged = 0
  let point = 0
  let thinUsd = 0
  let thinRecords = 0

  for (const v of values) {
    records += 1
    if (!v || v.usd == null) continue
    priced += 1

    /* ⚠️ A RECORD WITH NO RANGE STILL HAS A FLOOR AND A CEILING, AND THEY ARE
     * BOTH ITS POINT ESTIMATE. Summing `low` only where a `low` exists would
     * total the floor over a DIFFERENT population than the midpoint — a
     * smaller floor than midpoint for no reason a reader could ever discover.
     * The two shapes are counted separately (`ranged` / `point`) so the view
     * can say how much of the range is real spread. */
    floor = add(floor, v.low != null ? v.low : v.usd)
    mid = add(mid, v.usd)
    ceiling = add(ceiling, v.high != null ? v.high : v.usd)
    if (v.low != null && v.high != null) ranged += 1
    else point += 1

    /* ⚠️ THINNESS HAS TO SURVIVE INTO THE TOTAL. A sum is where a weak sample
     * hides best: one football row measured from 69 orders and one archery row
     * measured from 2 add up to a single confident-looking number. This tracks
     * what SHARE of the midpoint rests on fewer than COMPARABLE_N orders, so
     * the view can say it out loud. */
    const n = v.units?.n
    if (v.confidence?.key === 'sport-average' || (n != null && n < COMPARABLE_N)) {
      thinUsd += v.usd
      thinRecords += 1
    }
  }

  const unpriced = records - priced
  return {
    records,
    priced,
    unpriced,
    floor,
    mid,
    ceiling,
    ranged,
    point,
    thinUsd: priced ? thinUsd : null,
    thinRecords,
    /* A share only means something against a non-zero total. */
    thinShare: mid ? thinUsd / mid : null,
    hasFigure: priced > 0,
  }
}

/* =========================================================================
 * THE WORDS.
 *
 * 🔴 "A ceiling is EVERY record simultaneously at its maximum, which will not
 * happen." That sentence is the difference between a range and a forecast, and
 * without it this number gets quoted to a customer inside a day. It is the
 * same discipline the TAM chain already carries: a sum is a CEILING, not a
 * pipeline.
 * ========================================================================= */
export const TOTAL_COPY = {
  floor: 'if every one landed at its smallest observed size',
  mid: 'the midpoint — the figure to plan with',
  ceiling: 'if every one landed at its largest observed size, all at once. '
    + 'That will not happen; it is a ceiling, not a forecast.',
}

/**
 * The sentence naming the period this total covers.
 *
 * ⚠️ IT ALWAYS NAMES THE PERIOD, because the same three numbers mean
 * completely different things for "the next 30 days" and "every date", and a
 * total that does not say which is unreadable. `periodLabel` is the active
 * preset's own label, so the sentence and the chip can never disagree.
 */
export function totalScopeLine(total, { who = '', periodLabel = '', dated = true } = {}) {
  if (!total || !total.records) return ''
  const n = total.records.toLocaleString('en-US')
  /* ⚠️ "in Philip's book" IS OWNERSHIP LANGUAGE AND THIS REPO DOES NOT SAY IT —
   * `check-ownership-language` refused the first wording. Nobody has a book; a
   * rep holds an assignment for a period, which is exactly the distinction a
   * period sum should be making anyway. "assigned to Philip" is both the
   * accurate phrasing and the shorter one. */
  const whose = who ? ` assigned to ${who}` : ''
  const what = `${n} record${total.records === 1 ? '' : 's'}${whose}`
  if (!dated || !periodLabel || /^all dates$/i.test(periodLabel)) {
    return `${what}, every date`
  }
  return `${what} dated ${periodLabel.toLowerCase()}`
}

/**
 * 🔴 UNDATED RECORDS ARE THE TRAP IN A PERIOD SUM, AND THEY GET THEIR OWN
 * SENTENCE.
 *
 * Jennifer's entire cheer book carries no event dates. An undated record is
 * not "outside this period" — it sits on NO period at all, so no window will
 * ever show it and no period sum will ever include it. Folding it into
 * "outside this window" would let a rep read an empty month as a clear month
 * when it is actually missing data, which is the exact failure the three-way
 * in/out/undated count in the date filter already exists to prevent. The same
 * rule has to survive into the sum, or the sum re-introduces the bug the
 * filter fixed.
 *
 * Returns '' when nothing is undated — there is no sentence to write.
 */
export function undatedLine(undated, { total = 0 } = {}) {
  const u = Number(undated) || 0
  if (!u) return ''
  const n = u.toLocaleString('en-US')
  if (total && u >= total) {
    return `⚠️ Every one of these ${n} records carries NO event date, so none of them falls in any `
      + 'period and this sum covers nothing. That is missing data, not a clear calendar.'
  }
  return `${n} record${u === 1 ? '' : 's'} carry no event date at all, so they sit in no period and `
    + 'are in no sum. That is a gap in the records, not a quiet week.'
}

/**
 * What to say about the part of the book carrying no figure.
 *
 * ⚠️ NEVER A BARE COUNT. An unpriced record is a measurement gap, not a
 * worthless championship, and that distinction is the whole reason the
 * per-record reason text exists. A total that silently covers part of a book
 * is the defect that makes the surface untrustworthy.
 */
export function unpricedLine(total) {
  if (!total || !total.unpriced) return ''
  const u = total.unpriced.toLocaleString('en-US')
  const r = total.records.toLocaleString('en-US')
  return `${u} of ${r} in this period carry no figure yet and are NOT in this total — `
    + 'their sport and level have no measured unit history. That is a gap in what we have '
    + 'measured, not a set of championships worth nothing.'
}

/** What to say when a meaningful slice of the total rests on a thin sample. */
export function thinLine(total, { threshold = 0.1 } = {}) {
  if (!total || total.thinShare == null || total.thinShare < threshold) return ''
  const pct = Math.round(total.thinShare * 100)
  const n = total.thinRecords.toLocaleString('en-US')
  return `${pct}% of the midpoint rests on ${n} record${total.thinRecords === 1 ? '' : 's'} `
    + `measured from fewer than ${COMPARABLE_N} delivered orders — a sport-level average, `
    + 'not a comparable. Treat that share with suspicion.'
}
